import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseService } from '../base.service';
import { Client } from '@app/models/dashboard/client';
import { ApiAuthUrls } from '@app/core/utils/url-enums/auth.enum';
import { ClientListFilter } from '@app/models/dashboard/client-list-filter';
import moment from 'moment';
import { VATThreshold } from '@app/models/vat/vat-threshold';
import { MtdItsaRegistrationClientFilter } from '@app/models/itsa/mtd-itsa-registration-client';

@Injectable({
  providedIn: 'root'
})
export class ClientListService extends BaseService {

  constructor(
    private http: HttpClient
  ) {
    super();
  }


  updateCustomizeFieldsInDB() {
    const customisations = JSON.parse(localStorage.getItem('customisations'));
    let clientDashBoardCustomisedFields = '';
    let clientDashboardStickyFilterOptions = '';
    if (customisations && customisations.clientDashboardStickyFilterOptions) {
      clientDashboardStickyFilterOptions = customisations.clientDashboardStickyFilterOptions;
    } else {
      clientDashboardStickyFilterOptions = JSON.stringify({
        packages: [],
        nominalCodes: [],
        accountStatus: [],
        bankFeeds: [],
        lastBankRecStartDate: '',
        lastBankRecEndDate: '',
        vatReturnStartDate: '',
        vatReturnEndDate: '',
        accountPeriodStartDate: '',
        accountPeriodEndDate: '',
        clientManager: 0,
        clientGroup: 0,
        registeredIn : -1,
        itsaRegistered : -1,
        paidBy: [],
        licenseExpiryStartDate: '',
        licenseExpiryEndDate: '',
        loginBy: 0,
        fromLastLoginDate:'',
        toLastLoginDate:''
      });
    }

    if (customisations && customisations.clientDashBoardCustomisedFields) {
      clientDashBoardCustomisedFields = customisations.clientDashBoardCustomisedFields.toString();
    } else {
      clientDashBoardCustomisedFields = '';
    }


    const selectedSettings: { clientDashBoardCustomisedFields: string, clientDashboardStickyFilterOptions: string } = {
      clientDashBoardCustomisedFields,
      clientDashboardStickyFilterOptions
    };
    return this.http.put(
      `${this.API_URL}/settings/customisations`,
      JSON.stringify(selectedSettings),
      this.httpOptions
    );
  }

  getCustomizedStickyFilters() {
    const defaultFiltersOnError = {
      packages: [],
      nominalCodes: [],
      accountStatus: [],
      bankFeeds: [],
      lastBankRecStartDate: '',
      lastBankRecEndDate: '',
      vatReturnStartDate: '',
      vatReturnEndDate: '',
      accountPeriodStartDate: '',
      accountPeriodEndDate: '',
      clientManager: 0,
      clientGroup: 0,
      vatRegisteredInClients : [],
      registeredIn : -1,
      itsaRegistered : -1,
      paidBy: [],
      licenseExpiryStartDate: '',
      licenseExpiryEndDate: '',
      loginBy: 0,
      fromLastLoginDate:'',
      toLastLoginDate:''
    };
    let customisations;
    try {
      customisations = JSON.parse(localStorage.getItem('customisations'));
    } catch { }

    if (customisations && customisations.clientDashboardStickyFilterOptions) {
      try {
        return JSON.parse(customisations.clientDashboardStickyFilterOptions);
      } catch {
        return defaultFiltersOnError;
      }
    } else {
      return defaultFiltersOnError;
    }

  }

  getCustomizedFields() {
    let customisations;
    try {
      customisations = JSON.parse(localStorage.getItem('customisations'));
    } catch { }
    if (customisations && customisations.clientDashBoardCustomisedFields) {
      return customisations.clientDashBoardCustomisedFields.split(',');
    } else {
      return [];
    }
  }

  redirectToKashFlow(client) {
    return this.http.post(
      `${this.API_URL}/clients/${client.clientId}/session`, {}
    );
  }

  getVatThresholdStatus() {
    const headers = { 'content-type': 'application/json' }
    return this.http.post(this.API_URL + '/settings/single?keyName=VATThresholdNotification&value=0', { 'headers': headers });
  }

  getITSAThresholdStatus() {
    const headers = { 'content-type': 'application/json' }
    return this.http.post(this.API_URL + '/settings/single?keyName=ITSAThresholdNotification&value=0', { 'headers': headers });
  }

  getClients(
    partnerId: number,
    sortColumn: string,
    sortDirection: string,
    searchText: string = '',
    batchNumber: number,
    filterSet: ClientListFilter,
    isExport?: boolean,
  ): Observable<Client[]> {

    let packages = '';
    let accountType = '';
    let registeredIn = '';
    let nominalSet = '';
    let partnerManagerId = 0;
    let partnerGroupId = 0;
    let bankfeeds = '';
    let lastbankrecstartdate = '';
    let lastbankrecenddate = '';
    let vatreturnstartdate = '';
    let vatreturnenddate = '';
    let accountingperiodstartdate = '';
    let accountingperiodenddate = '';
    let itsaRegistered = '';
    let paidBy = '';
    let licenseExpiryStartDate = '';
    let licenseExpiryEndDate = '';
    let loginBy = ''
    let fromLastLoginDate = '';
    let toLastLoginDate = '';

    packages = filterSet.packages.toString();

    //To get package values based on selected packages
    let selectedPackageValues = Array.from(packages.split(","));
    let updatedPackageValues = selectedPackageValues.map(this.getPackage)
    packages = encodeURIComponent(updatedPackageValues.join());

    accountType = filterSet.accountStatus.toString();
    nominalSet = filterSet.nominalCodes.toString();
    registeredIn = (filterSet.registeredIn != null && filterSet.registeredIn !== undefined) ? filterSet.registeredIn.toString() : "-1";
    itsaRegistered=(filterSet.itsaRegistered != null && filterSet.itsaRegistered !== undefined) ? filterSet.itsaRegistered.toString() : "-1";
    loginBy = (filterSet.loginBy != null && filterSet.loginBy !== undefined) ? filterSet.loginBy.toString() : "0";

    partnerManagerId = filterSet.clientManager ? filterSet.clientManager : null;
    partnerGroupId = filterSet.clientGroup ? filterSet.clientGroup : null;

    bankfeeds = filterSet.bankFeeds.toString();
    lastbankrecstartdate = this.formatDate(filterSet.lastBankRecStartDate);
    lastbankrecenddate = this.formatDate(filterSet.lastBankRecEndDate);
    vatreturnstartdate = this.formatDate(filterSet.vatReturnStartDate);
    vatreturnenddate = this.formatDate(filterSet.vatReturnEndDate);
    accountingperiodstartdate = this.formatDate(filterSet.accountPeriodStartDate);
    accountingperiodenddate = this.formatDate(filterSet.accountPeriodEndDate);
    paidBy = filterSet.paidBy === undefined? '' : filterSet.paidBy.toString();
    licenseExpiryStartDate = this.formatDate(filterSet.licenseExpiryStartDate);
    licenseExpiryEndDate = this.formatDate(filterSet.licenseExpiryEndDate);
    fromLastLoginDate = this.formatDate(filterSet.fromLastLoginDate);
    toLastLoginDate = this.formatDate(filterSet.toLastLoginDate);
   
    sortColumn = sortColumn === 'packageDescription' ? 'package' : sortColumn;
    sortColumn = sortColumn === 'lastBankReconciliation' ? 'reconciliationDate' : sortColumn;
    sortColumn = sortColumn === 'lastBankReconciliation' ? 'reconciliationDate' : sortColumn;
    sortColumn = sortColumn === 'vatReturnDueDate' ? 'vatReturnDate' : sortColumn;
    sortColumn = sortColumn === 'subscriptionExpiryDate' ? 'licenseExpiryDate' : sortColumn;
    sortColumn = sortColumn === 'accountStatusDescription' ? 'accountStatus' : sortColumn;
    sortColumn = sortColumn === 'registeredInDescription' ? 'registeredIn' : sortColumn;
    if (isExport) {
      // tslint:disable-next-line:max-line-length
      return this.http.get<any>(`${this.API_URL}/${ApiAuthUrls.getClients}?sortColumn=${sortColumn}&sortDirection=${sortDirection}&searchText=${searchText}&registeredIn=${registeredIn}&package=${packages}&accountType=${accountType}&nominalSet=${nominalSet}&partnerManagerId=${partnerManagerId}&partnerGroupId=${partnerGroupId}&batchNumber=${batchNumber}&bankfeeds=${bankfeeds}&lastbankrecstartdate=${lastbankrecstartdate}&lastbankrecenddate=${lastbankrecenddate}&vatreturnstartdate=${vatreturnstartdate}&vatreturnenddate=${vatreturnenddate}&accountingperiodstartdate=${accountingperiodstartdate}&accountingperiodenddate=${accountingperiodenddate}&isExport=${isExport}&itsaRegistered=${itsaRegistered}&paidBy=${paidBy}&expiryStartDate=${licenseExpiryStartDate}&expiryEndDate=${licenseExpiryEndDate}&loginBy=${loginBy}&fromLastLoginDate=${fromLastLoginDate}&toLastLoginDate=${toLastLoginDate}`
      );
    } else {
      // tslint:disable-next-line:max-line-length
      return this.http.get<any>(`${this.API_URL}/${ApiAuthUrls.getClients}?sortColumn=${sortColumn}&sortDirection=${sortDirection}&searchText=${searchText}&registeredIn=${registeredIn}&package=${packages}&accountType=${accountType}&nominalSet=${nominalSet}&partnerManagerId=${partnerManagerId}&partnerGroupId=${partnerGroupId}&batchNumber=${batchNumber}&bankfeeds=${bankfeeds}&lastbankrecstartdate=${lastbankrecstartdate}&lastbankrecenddate=${lastbankrecenddate}&vatreturnstartdate=${vatreturnstartdate}&vatreturnenddate=${vatreturnenddate}&accountingperiodstartdate=${accountingperiodstartdate}&accountingperiodenddate=${accountingperiodenddate}&itsaRegistered=${itsaRegistered}&paidBy=${paidBy}&expiryStartDate=${licenseExpiryStartDate}&expiryEndDate=${licenseExpiryEndDate}&loginBy=${loginBy}&fromLastLoginDate=${fromLastLoginDate}&toLastLoginDate=${toLastLoginDate}`
      );
    }
  }

  formatDate(_date) {
    return _date ? moment(_date).format('YYYY-MM-DD') : _date;
  }


  getSearchAutoFillData(
    partnerId: number,
    searchText: string
  ) {
    return this.http.get(
      `${this.API_URL}/${
      ApiAuthUrls.getClients}/${partnerId}/search?searchText=${searchText}`
    );
  }

  getVatRegisteredClients(
    sortDirection: string,
    searchText: string = '',
    batchNumber: number
  ): Observable<Client[]> {
    // tslint:disable-next-line:max-line-length
    return this.http.get<any>(`${this.API_URL}/${ApiAuthUrls.getClients}?sortDirection=${sortDirection}&searchText=${searchText}&batchNumber=${batchNumber}&vatregistered=1`);
  }

  getNonMtdVatRegisteredClients(
    sortDirection: string,
    searchText: string = '',
    batchNumber: number
  ): Observable<Client[]> {
    return this.http.get<any>(`${this.API_URL}/${ApiAuthUrls.getClients}?sortDirection=${sortDirection}&searchText=${searchText}&batchNumber=${batchNumber}&vatregistered=1&nonmtdclientsonly=true`);
  }

  getNonMtdItsaRegisteredClients(
    sortDirection: string,
    searchText: string = '',
    batchNumber: number
  ): Observable<Client[]> {
    return this.http.get<any>(`${this.API_URL}/${ApiAuthUrls.getClients}?sortDirection=${sortDirection}&searchText=${searchText}&batchNumber=${batchNumber}&itsaRegistered=0&nonMtdItsaClientsOnly=true`);
  }

  getVatThresholdClients(
    searchText: string = '',
    batchNumber: number,
    sortColumn: string ='',
    sortDirection: string='',
    vatThreshold:string=''
  ): Observable<VATThreshold[]> {
    return this.http.get<any>(`${this.API_URL}/${ApiAuthUrls.vatThreshold}?searchText=${searchText}&batchNumber=${batchNumber}&sortColumn=${sortColumn}&sortDirection=${sortDirection}&vatThreshold=${vatThreshold}`);
  }
   getPackage(packages) {
    switch (packages) {
      case "2": return "Starter";
      case "3": return "Business";
      case "4": return "Business + Payroll";
      case "13": return "IRIS Elements Cashbook";
      default: return "";
    }
  }

  getMtdItsaClientsForregistration(filters: MtdItsaRegistrationClientFilter): Observable<any> {
    return this.http.get<any>(`${this.API_URL}/clients/mtditsa/registration?searchtext=${filters.SearchText}&thresholdlevel=${filters.ThresholdLevel}&sortby=${filters.SortingOn}&sortdirection=${filters.SortDirection}`);
  }
}
